import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight, Shield } from 'lucide-react'
import { useAuth } from '@/lib/auth/useAuth'
import { PageHeader } from '@/components/page-header'
import { Eyebrow, Hairline } from '@/components/hawk-primitives'

export const Route = createFileRoute('/_app/settings/')({
  component: SettingsPage,
})

function SettingsPage() {
  const { user, roles } = useAuth()
  const groups = user?.groups ?? []
  const initial = (user?.name ?? user?.email ?? 'U').charAt(0).toUpperCase()

  return (
    <div className="px-10 py-8">
      <PageHeader
        title="Profile"
        description="Identity attributes provisioned by your SSO provider. Edits must be made upstream."
      />

      <section className="animate-hawk-rise" style={{ animationDelay: '0ms' }}>
        <div className="mb-4">
          <Eyebrow>Identity</Eyebrow>
        </div>

        <div className="relative border border-border bg-card rounded-md overflow-hidden">
          <div
            className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent pointer-events-none"
            aria-hidden
          />

          <div className="flex items-center gap-5 p-7 border-b border-border">
            <div className="size-16 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center text-3xl text-primary font-medium shrink-0">
              {initial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-2xl font-semibold tracking-tight text-foreground leading-tight mb-1">
                {user?.name ?? '—'}
              </div>
              <div className="font-mono text-xs text-muted-foreground truncate">
                {user?.email ?? '—'}
              </div>
            </div>
          </div>

          <dl className="p-7 grid grid-cols-[140px_1fr] gap-y-4 text-sm">
            <KV label="Subject" mono breakable value={user?.sub ?? '—'} />
            <KV
              label="Roles"
              value={
                roles.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {roles.map((role) => (
                      <span
                        key={role}
                        className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded bg-primary/10 text-primary ring-1 ring-inset ring-primary/30"
                      >
                        <span className="size-1 rounded-full bg-primary" />
                        {role}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted-foreground">None assigned</span>
                )
              }
            />
            <KV
              label="Groups"
              value={
                groups.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {groups.map((group) => (
                      <span
                        key={group}
                        className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded bg-secondary text-foreground/70 ring-1 ring-inset ring-border"
                      >
                        {group}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )
              }
            />
          </dl>

          <div className="px-7 pb-5">
            <Hairline />
            <p className="mt-4 text-xs text-muted-foreground leading-relaxed">
              Identity attributes are managed by your identity provider via SSO.
              To change them, contact your administrator.
            </p>
          </div>
        </div>
      </section>

      <section
        className="mt-10 animate-hawk-rise"
        style={{ animationDelay: '80ms' }}
      >
        <div className="mb-4">
          <Eyebrow>Related</Eyebrow>
        </div>
        <Link
          to="/settings/security"
          className="group flex items-center gap-4 border border-border bg-card rounded-md p-5 hover:border-primary/30 transition-colors"
        >
          <div className="size-10 rounded-md bg-secondary border border-border flex items-center justify-center group-hover:border-primary/40 transition-colors">
            <Shield className="size-4 text-muted-foreground group-hover:text-primary transition-colors" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium">Security keys & sessions</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Manage WebAuthn credentials and active sessions
            </div>
          </div>
          <ArrowRight className="size-4 text-muted-foreground/50 group-hover:translate-x-0.5 group-hover:text-foreground transition-all" />
        </Link>
      </section>
    </div>
  )
}

function KV({
  label,
  value,
  mono,
  breakable,
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
  breakable?: boolean
}) {
  return (
    <>
      <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground pt-1">
        {label}
      </dt>
      <dd
        className={[
          'text-foreground',
          mono ? 'font-mono text-xs text-foreground/80' : 'text-sm',
          breakable ? 'break-all' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {value}
      </dd>
    </>
  )
}
